import React, { useEffect, useRef, useState } from 'react';
import { useConfirm, useToast } from '../context/UIContext';
import {
    createBwaImport, deleteBwaImport, getBwaDetail,
    listBwaImports, parseBwaPdf, saveBwaRows,
} from '../services/BwaService';
import { extractApiError } from '../utils/apiError';
import '../styles/BwaDashboard.css';

const MONTHS = [
    'Januar','Februar','März','April','Mai','Juni',
    'Juli','August','September','Oktober','November','Dezember',
];

const MONTHS_SHORT = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

// Parse a German-formatted number string (e.g. "46.800,89" or "-4.621,33")
function parseDE(str) {
    if (!str || str.trim() === '' || str.trim() === '-') return null;
    const cleaned = str.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

function fmtInput(n) {
    if (n == null) return '';
    return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function NumCell({ value, onChange }) {
    const [text, setText] = useState(fmtInput(value));
    const prevValue = useRef(value);

    // Sync when external value changes (e.g. after save)
    useEffect(() => {
        if (value !== prevValue.current) {
            setText(fmtInput(value));
            prevValue.current = value;
        }
    }, [value]);

    return (
        <td className="bwa-col-num">
            <input
                className="bwa-input"
                value={text}
                onChange={e => setText(e.target.value)}
                onBlur={() => {
                    const parsed = parseDE(text);
                    onChange(parsed);
                    setText(fmtInput(parsed));
                    prevValue.current = parsed;
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') e.target.blur();
                }}
            />
        </td>
    );
}

export default function BwaDashboard() {
    const toast   = useToast();
    const confirm = useConfirm();

    const [imports,    setImports]    = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [detail,     setDetail]     = useState(null);   // BwaImportDetailDTO
    const [rows,       setRows]       = useState([]);     // local editable copy
    const [dirty,      setDirty]      = useState(false);
    const [saving,     setSaving]     = useState(false);

    const fileInputRef = useRef(null);

    const currentYear = new Date().getFullYear();
    const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
    const [newYear,  setNewYear]  = useState(currentYear);

    const reload = async () => {
        const data = await listBwaImports();
        const sorted = [...data].sort((a, b) =>
            a.year !== b.year ? a.year - b.year : a.month - b.month,
        );
        setImports(sorted);
        return sorted;
    };

    useEffect(() => {
        reload()
            .then(sorted => {
                if (sorted.length) select(sorted[sorted.length - 1].id);
            })
            .catch(() => toast('BWA-Daten konnten nicht geladen werden.'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const select = (id) => {
        setSelectedId(id);
        setDetail(null);
        setRows([]);
        setDirty(false);
        getBwaDetail(id)
            .then(d => {
                setDetail(d);
                setRows(d.rows.map(r => ({ ...r })));
            })
            .catch(() => toast('Daten konnten nicht geladen werden.'));
    };

    const updateRow = (rowId, field, value) => {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, [field]: value } : r));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = rows.map(r => ({
                id:          r.id,
                month_value: r.month_value,
                month_pct:   r.month_pct,
            }));
            const updated = await saveBwaRows(selectedId, payload);
            setDetail(updated);
            setRows(updated.rows.map(r => ({ ...r })));
            setDirty(false);
            toast('Gespeichert.', 'success');
        } catch (err) {
            toast(extractApiError(err, 'Speichern fehlgeschlagen.'));
        } finally {
            setSaving(false);
        }
    };

    const handleCreate = async () => {
        try {
            const d = await createBwaImport(newMonth, newYear, null);
            const sorted = await reload();
            select(d.id);
            toast(`${MONTHS[d.month - 1]} ${d.year} angelegt.`, 'success');
        } catch (err) {
            toast(extractApiError(err, 'Anlegen fehlgeschlagen.'));
        }
    };

    const handleDelete = async (imp) => {
        const lbl = `${MONTHS_SHORT[imp.month - 1]} ${imp.year}`;
        if (!await confirm(`BWA "${lbl}" wirklich löschen?`, { confirmLabel: 'Löschen', danger: true })) return;
        try {
            await deleteBwaImport(imp.id);
            const sorted = await reload();
            if (selectedId === imp.id) {
                if (sorted.length) select(sorted[sorted.length - 1].id);
                else { setSelectedId(null); setDetail(null); setRows([]); }
            }
        } catch (err) {
            toast(extractApiError(err, 'Löschen fehlgeschlagen.'));
        }
    };

    const mergeAndActivate = (detail, parsedRows) => {
        const norm = s => s.toLowerCase().replace(/\s+/g, ' ').trim();
        const byLabel = {};
        parsedRows.forEach(pr => { byLabel[norm(pr.label)] = pr; });
        const hasMatches = detail.rows.some(r => byLabel[norm(r.label)]);

        const updated = detail.rows.map((r, i) => {
            const pr = hasMatches ? byLabel[norm(r.label)] : parsedRows[i];
            if (!pr) return { ...r };
            return { ...r, month_value: pr.month_value ?? null, month_pct: pr.month_pct ?? null };
        });

        setSelectedId(detail.id);
        setDetail(detail);
        setRows(updated);
        setDirty(true);
        return updated.filter(r => r.month_value != null).length;
    };

    const handlePdfImport = async (file) => {
        try {
            const result = await parseBwaPdf(file);
            const { month, year, rows: parsedRows } = result;

            if (!month || !year) {
                toast('Monat/Jahr konnte nicht erkannt werden.');
                return;
            }

            // Use the imports list captured in this render; reload if needed
            const existing = imports.find(i => i.month === month && i.year === year);
            let detail;
            if (existing) {
                detail = await getBwaDetail(existing.id);
            } else {
                detail = await createBwaImport(month, year, null);
                await reload();
            }

            const filled = mergeAndActivate(detail, parsedRows);
            toast(`${MONTHS[month - 1]} ${year} – ${filled} Werte importiert.`, 'success');
        } catch (err) {
            toast(extractApiError(err, 'PDF konnte nicht importiert werden.'));
        }
    };

    const handleCsvImport = (file) => {
        if (!detail) { toast('Bitte erst einen Monat auswählen oder anlegen.'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const text = ev.target.result;
                const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
                if (!lines.length) { toast('Datei ist leer.'); return; }
                const sep = lines[0].includes('\t') ? '\t' : ';';
                const parsed = lines
                    .map(line => {
                        const p = line.split(sep);
                        if (p.length < 2) return null;
                        return { label: p[0], month_value: parseDE(p[1]), month_pct: p[2] ? parseDE(p[2]) : null };
                    })
                    .filter(Boolean);
                const filled = mergeAndActivate(detail, parsed);
                toast(`${filled} Werte importiert.`, 'success');
            } catch {
                toast('Datei konnte nicht gelesen werden.');
            }
        };
        reader.readAsText(file, 'utf-8');
    };

    const handleFileImport = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (file.name.toLowerCase().endsWith('.pdf')) {
            if (file.type && file.type !== 'application/pdf') {
                toast('Bitte eine gültige PDF-Datei auswählen.');
                return;
            }
            await handlePdfImport(file);
        } else if (
            file.name.toLowerCase().endsWith('.csv') ||
            file.name.toLowerCase().endsWith('.tsv') ||
            file.name.toLowerCase().endsWith('.txt')
        ) {
            handleCsvImport(file);
        } else {
            toast('Nicht unterstütztes Dateiformat. Bitte eine PDF-, CSV- oder TSV-Datei auswählen.');
        }
    };

    return (
        <div className="bwa-dash">

            <div className="bwa-topbar">
                <div className="bwa-pills">
                    {imports.map(imp => (
                        <button
                            key={imp.id}
                            className={`bwa-pill${selectedId === imp.id ? ' active' : ''}`}
                            onClick={() => select(imp.id)}
                        >
                            {MONTHS_SHORT[imp.month - 1]} {imp.year}
                            <span
                                className="bwa-pill-x"
                                title="Löschen"
                                onClick={ev => { ev.stopPropagation(); handleDelete(imp); }}
                            >×</span>
                        </button>
                    ))}
                </div>

                <div className="bwa-new-form">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.csv,.tsv,.txt"
                        style={{ display: 'none' }}
                        onChange={handleFileImport}
                    />
                    <button className="inv-btn" onClick={() => fileInputRef.current?.click()}>
                        Importieren
                    </button>
                    <select value={newMonth} onChange={e => setNewMonth(+e.target.value)}>
                        {MONTHS.map((m, i) => (
                            <option key={i + 1} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <input
                        type="number"
                        value={newYear}
                        min={2000}
                        max={2100}
                        onChange={e => setNewYear(+e.target.value)}
                    />
                    <button className="inv-btn primary" onClick={handleCreate}>
                        + Monat anlegen
                    </button>
                </div>
            </div>

            {!imports.length && (
                <div className="bwa-empty">
                    <div className="bwa-empty-icon">
                        <svg viewBox="0 0 48 48" fill="none">
                            <path d="M12 6h16l8 8v28H12V6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            <path d="M28 6v8h8M18 20h12M18 26h12M18 32h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <p className="bwa-empty-title">Noch keine BWA angelegt</p>
                    <p className="bwa-empty-sub">Wählen Sie Monat und Jahr und klicken Sie "+ Monat anlegen".</p>
                </div>
            )}

            {detail && (
                <div className="bwa-save-bar">
                    <div>
                        <div className="bwa-save-title">
                            {MONTHS[detail.month - 1]} {detail.year}
                            {detail.label ? ` – ${detail.label}` : ''}
                        </div>
                        <div className="bwa-save-hint">
                            Werte direkt in der Tabelle bearbeiten, dann speichern.
                        </div>
                    </div>
                    <button
                        className="inv-btn primary"
                        onClick={handleSave}
                        disabled={!dirty || saving}
                    >
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                </div>
            )}

            {rows.length > 0 && (
                <div className="bwa-table-wrap">
                    <table className="bwa-table">
                        <thead>
                            <tr>
                                <th className="bwa-col-label">Position</th>
                                <th className="bwa-col-num">Monat €</th>
                                <th className="bwa-col-num">% GL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id} className={`bwa-row${row.is_subtotal ? ' bwa-row--sub' : ''}`}>
                                    <td className="bwa-col-label">{row.label}</td>
                                    <NumCell
                                        value={row.month_value}
                                        onChange={v => updateRow(row.id, 'month_value', v)}
                                    />
                                    <NumCell
                                        value={row.month_pct}
                                        onChange={v => updateRow(row.id, 'month_pct', v)}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedId && !detail && (
                <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                    Lade…
                </p>
            )}
        </div>
    );
}
